import { PropsWithChildren, ReactNode } from 'react'
import { BaseModal, BaseModalProps } from 'components/organisms/modals/base-modal'

type ContentModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full'
type BaseModalSize = NonNullable<BaseModalProps['size']>

export interface ContentModalProps {
	open: boolean
	title?: ReactNode
	setOpen?: (open: boolean) => void
	isModal?: boolean
	hideCloseButton?: boolean
	hideHeader?: boolean
	size?: ContentModalSize | string
	className?: string
	/**
	 * Renders a full-width action block above the modal content (e.g. a toolbar with gear + close).
	 * Receives `handleClose` — call it to close the modal through the full close sequence.
	 * When provided, the default close button is suppressed.
	 */
	customTopActionBlock?: (onClose: () => void) => ReactNode
	onClickClose?: () => void
	suppressClickClose?: boolean
	customSize?: string
	bodyClassName?: string
}

const sizeMapping: Record<ContentModalSize, BaseModalSize> = {
	sm: 'md',
	md: 'lg',
	lg: 'xl',
	xl: '2xl',
	'2xl': '3xl',
	'3xl': '4xl',
	'4xl': '5xl',
	'5xl': '6xl',
	'6xl': '7xl',
	'7xl': 'full',
	full: 'full',
}

export function ContentModal({
	children,
	open,
	setOpen,
	title = '',
	isModal = false,
	hideCloseButton = false,
	size = '',
	className = '',
	customTopActionBlock,
	onClickClose,
	suppressClickClose,
	hideHeader = false,
	customSize = '',
	bodyClassName = '',
}: PropsWithChildren<ContentModalProps>) {
	const handleClose = () => {
		onClickClose?.()
		// Always close the modal unless suppressClickClose is true
		if (!suppressClickClose) {
			setOpen?.(false)
		}
	}

	const modalSize: BaseModalSize = size && size in sizeMapping ? sizeMapping[size as ContentModalSize] : 'md'

	return (
		<BaseModal
			open={open}
			setOpen={setOpen}
			title={title}
			hideCloseButton={hideCloseButton}
			hideHeader={hideHeader}
			size={modalSize}
			customSize={customSize}
			className={className}
			bodyClassName={bodyClassName}
			preventClose={isModal}
			onClose={handleClose}
			customTopActionBlock={customTopActionBlock}
		>
			{children}
		</BaseModal>
	)
}
